import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Download as DownloadIcon, Mail, Phone, Search, Scale, Users, WalletCards } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { useJob } from '../context/JobContext';
import api from '../services/api';
import LoaderProgress from '../components/ui/loader-progress';

const StyledCard = styled(Card)(({ theme }) => ({
  boxShadow:
    '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden',
  backgroundColor: theme.palette.background.paper,
}));

const formatCurrency = (value) =>
  new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

  const getNumberColor = (value) => (Number(value || 0) > 0 ? 'success.main' : 'error.main');

const formatDate = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('it-IT');
};

const StatWidget = ({ title, value, icon: Icon, color }) => (
  <Paper
    sx={{
      p: 2,
      display: 'flex',
      alignItems: 'center',
      height: '100%',
      borderColor: 'rgba(145, 158, 171, 0.24)',
    }}
    elevation={0}
    variant="outlined"
  >
    <Box
      sx={{
        p: 1.5,
        borderRadius: '50%',
        bgcolor: `${color}15`,
        color,
        mr: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Icon size={22} />
    </Box>
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
        {title}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 800 }}>
        {value}
      </Typography>
    </Box>
  </Paper>
);

const columns = [
  { key: 'ragione_sociale', label: 'Cliente', sortable: true, align: 'left' },
  { key: 'codice', label: 'Codice', sortable: true, align: 'left' },
  { key: 'piva_cf', label: 'P.IVA / CF', sortable: true, align: 'left' },
  { key: 'anno', label: 'Ultimo anno', sortable: true, align: 'left' },
  { key: 'documenti_totali', label: 'Documenti', sortable: true, align: 'left' },
  { key: 'contratti_upsell_count', label: 'Contr./Upsell', sortable: true, align: 'left' },
  { key: 'importo_totale_documenti', label: 'Importo Documenti', sortable: true, align: 'right' },
  { key: 'fatturato_importo', label: 'Fatturato', sortable: true, align: 'right' },
  { key: 'transazioni_importo', label: 'Transazioni', sortable: true, align: 'right' },
  { key: 'contratti_importo', label: 'Contratti', sortable: true, align: 'right' },
  { key: 'upsell_importo', label: 'Upsell', sortable: true, align: 'right' },
  { key: 'saldo_contabile', label: 'Saldo', sortable: true, align: 'right' },
  { key: 'ultimo_documento_data', label: 'Ultimo documento', sortable: true, align: 'left' },
];

export default function PasspSituazioneContabile() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addJob } = useJob();
  const roleName = (user?.role_name || user?.role || '').toLowerCase();
  const isAdmin = roleName === 'admin';

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState({
    clienti_filtrati: 0,
    documenti_totali: 0,
    contratti_importo: 0,
    saldo_contabile: 0,
  });
  const [searchInput, setSearchInput] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [azienda, setAzienda] = useState('all');
  const [anno, setAnno] = useState('all');
  const [tipoSoggetto, setTipoSoggetto] = useState('all');
  const [aziendeOptions, setAziendeOptions] = useState([]);
  const [anniOptions, setAnniOptions] = useState([]);
  const [orderBy, setOrderBy] = useState('ragione_sociale');
  const [order, setOrder] = useState('asc');

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchValue(searchInput.trim());
      setPage(0);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const res = await api.get('/passp-esposizione-contabile/filters/options');
        setAziendeOptions(res.data?.aziende || []);
        setAnniOptions(res.data?.anni || []);
      } catch (_error) {
        toast.error('Errore nel caricamento filtri Situazione Clienti');
      }
    };
    fetchFilters();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = {
          page: page + 1,
          limit: rowsPerPage,
          orderBy,
          orderDirection: order,
        };
        if (searchValue) params.search = searchValue;
        if (azienda !== 'all') params.azienda = azienda;
        if (anno !== 'all') params.anno = anno;
        if (tipoSoggetto !== 'all') params.tipoSoggetto = tipoSoggetto;

        const res = await api.get('/passp-esposizione-contabile/situazione-contabile', { params });
        setRows(Array.isArray(res.data?.data) ? res.data.data : []);
        setTotal(Number(res.data?.total || 0));
        setSummary({
          clienti_filtrati: Number(res.data?.summary?.clienti_filtrati || res.data?.total || 0),
          documenti_totali: Number(res.data?.summary?.documenti_totali || 0),
          contratti_importo: Number(res.data?.summary?.contratti_importo || 0),
          saldo_contabile: Number(res.data?.summary?.saldo_contabile || 0),
        });
      } catch (_error) {
        toast.error('Errore nel caricamento Situazione Clienti');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [page, rowsPerPage, orderBy, order, searchValue, azienda, anno, tipoSoggetto]);

  const handleSort = (column) => {
    const isAsc = orderBy === column && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(column);
    setPage(0);
  };

  const openSubjectDetail = (row) => {
    const key = String(row?.soggetto_key || '').trim();
    if (!key) {
      toast.error('Dettaglio nominativo non disponibile');
      return;
    }
    navigate(`/passp-esposizione-contabile/${encodeURIComponent(key)}`);
  };

  const handleExportCsv = async () => {
    if (exporting) return;

    setExporting(true);
    try {
      const payload = {
        orderBy,
        orderDirection: order,
      };
      if (searchValue) payload.search = searchValue;
      if (azienda !== 'all') payload.azienda = azienda;
      if (anno !== 'all') payload.anno = anno;
      if (tipoSoggetto !== 'all') payload.tipoSoggetto = tipoSoggetto;

      const res = await api.post('/passp-esposizione-contabile/situazione-contabile/export', payload);
      if (res.data?.jobId) {
        addJob(res.data.jobId, 'passp_situazione_contabile_csv');
        toast.info('Esportazione CSV avviata. Il file sarà disponibile in campanella.', {
          position: 'top-right',
          autoClose: 4000,
        });
      } else {
        toast.error('Impossibile avviare esportazione CSV');
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Errore durante avvio esportazione CSV');
    } finally {
      setExporting(false);
    }
  };

  const empty = !loading && rows.length === 0;

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          mb: 3,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: '50%',
              bgcolor: '#eef2ff',
              color: '#4f46e5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Scale size={20} />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              Passpartout • Situazione Clienti
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Sintesi per cliente di esposizione, fatturato, contratti/upsell e saldo
            </Typography>
          </Box>
        </Box>

        {isAdmin && (
          <Button
            variant="outlined"
            startIcon={<DownloadIcon size={18} />}
            onClick={handleExportCsv}
            disabled={exporting}
            sx={{ textTransform: 'none' }}
          >
            {exporting ? 'Esportazione...' : 'Esporta'}
          </Button>
        )}
      </Box>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatWidget title="Clienti filtrati" value={summary.clienti_filtrati} icon={Users} color="#0ea5e9" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatWidget title="Documenti filtrati" value={summary.documenti_totali} icon={WalletCards} color="#6366f1" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatWidget
            title="Contratti filtrati"
            value={formatCurrency(summary.contratti_importo)}
            icon={Scale}
            color="#22c55e"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatWidget title="Saldo filtrato" value={formatCurrency(summary.saldo_contabile)} icon={Scale} color="#f59e0b" />
        </Grid>
      </Grid>

      <StyledCard>
        <Box
          sx={{
            px: 3,
            py: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <TextField
            size="small"
            placeholder="Cerca per cliente, codice, CF o P.IVA..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={18} />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 280 }}
          />

          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Azienda</InputLabel>
            <Select
              value={azienda}
              label="Azienda"
              onChange={(e) => {
                setAzienda(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="all">Tutte</MenuItem>
              {aziendeOptions.map((item) => (
                <MenuItem key={item.code} value={item.code}>
                  {item.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Anno</InputLabel>
            <Select
              value={anno}
              label="Anno"
              onChange={(e) => {
                setAnno(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="all">Tutti</MenuItem>
              {anniOptions.map((item) => (
                <MenuItem key={String(item)} value={String(item)}>
                  {item}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Tipo soggetto</InputLabel>
            <Select
              value={tipoSoggetto}
              label="Tipo soggetto"
              onChange={(e) => {
                setTipoSoggetto(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="all">Tutti</MenuItem>
              <MenuItem value="azienda">Azienda</MenuItem>
              <MenuItem value="persona_fisica">Persona fisica</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'background.neutral' }}>
                {columns.map((col) => (
                  <TableCell key={col.key} align={col.align || 'left'}>
                    {col.sortable ? (
                      <TableSortLabel
                        active={orderBy === col.key}
                        direction={orderBy === col.key ? order : 'asc'}
                        onClick={() => handleSort(col.key)}
                      >
                        {col.label}
                      </TableSortLabel>
                    ) : (
                      col.label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={columns.length}>
                    <LoaderProgress />
                  </TableCell>
                </TableRow>
              )}

              {empty && (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center">
                    <Box sx={{ py: 5 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        Nessun cliente trovato
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}

              {!loading &&
                rows.map((row) => {
                  const clienteLabel =
                    row.ragione_sociale ||
                    row.denominazione ||
                    [row.cognome, row.nome].filter(Boolean).join(' ').trim() ||
                    '-';
                  const emailLabel = String(row.email || '').trim();
                  const telefonoLabel = String(row.telefono || '').trim();
                  return (
                    <TableRow
                      key={row.id}
                      hover
                      onClick={() => openSubjectDetail(row)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        <Stack spacing={0.8}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            <Box
                              component="span"
                              sx={{
                                color: 'primary.main',
                                '&:hover': { textDecoration: 'underline' },
                              }}
                            >
                              {clienteLabel}
                            </Box>
                          </Typography>
                          <Stack direction="row" spacing={0.9} alignItems="center">
                            <Mail size={16} color="#64748b" />
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                              {emailLabel || '-'}
                            </Typography>
                          </Stack>
                          <Stack direction="row" spacing={0.9} alignItems="center">
                            <Phone size={16} color="#64748b" />
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                              {telefonoLabel || '-'}
                            </Typography>
                          </Stack>
                        </Stack>
                      </TableCell>
                      <TableCell>{row.codice || '-'}</TableCell>
                      <TableCell>{row.partita_iva || row.codice_fiscale || '-'}</TableCell>
                      <TableCell>{row.anno || '-'}</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: getNumberColor(row.documenti_totali) }}>
                        {Number(row.documenti_totali || 0)}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, color: getNumberColor(row.contratti_upsell_count) }}>
                        {Number(row.contratti_upsell_count || 0)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: getNumberColor(row.importo_totale_documenti) }}>
                        {formatCurrency(row.importo_totale_documenti)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: getNumberColor(row.fatturato_importo) }}>
                        {formatCurrency(row.fatturato_importo)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: getNumberColor(row.transazioni_importo) }}>
                        {formatCurrency(row.transazioni_importo)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: getNumberColor(row.contratti_importo) }}>
                        {formatCurrency(row.contratti_importo)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: getNumberColor(row.upsell_importo) }}>
                        {formatCurrency(row.upsell_importo)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontWeight: 700,
                          color: Number(row.saldo_contabile || 0) > 0 ? 'success.main' : 'error.main',
                        }}
                      >
                        {formatCurrency(row.saldo_contabile)}
                      </TableCell>
                      <TableCell>{formatDate(row.ultimo_documento_data)}</TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_event, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Righe per pagina"
        />
      </StyledCard>
    </Box>
  );
}
